import * as Popover from '@radix-ui/react-popover';
import clsx from 'clsx';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom';
import type { ApiUser, ConversationListItem, Product } from '../api';
import { api } from '../api';
import {
  useAutomations,
  useCreateConversation,
  useDeleteConversation,
  useModels,
  usePatchConversation,
  useProjectConversationCounts,
  useProjects,
  useRecentConversations,
  useSkills,
} from '../hooks/queries';
import { stopAllChatStreams, useBusyConversationIds } from '../hooks/useChatStream';
import { type TranslationKey, useLanguage, useT } from '../i18n';
import { conversationTitle } from '../lib/conversationTitle';
import { conversationDay, type GroupKey, groupOf, metaOf } from '../lib/historyGroups';
import { projectSkills, skillSelectionKey } from '../lib/projectSkills';
import { nameFromEmail } from '../lib/userName';
import { unsubscribeThisDevice } from '../push';
import { useDrafts } from '../stores/drafts';
import { useUi } from '../stores/ui';
import { AccountAvatar } from './AccountAvatar';
import { BrandIdentity, type Branding } from './BrandIdentity';
import { useConfirmDialog, usePromptDialog } from './ConfirmDialog';
import { LogoChip } from './LogoChip';
import { MenuIcon } from './MenuIcon';
import { ReleaseNotesDialog } from './ReleaseNotesDialog';
import { SidebarResizeHandle } from './SidebarResizeHandle';
import { SHELL_COPY } from './shellCopy';
import { UsageBreakdownPanel } from './UsageBreakdown';

type NavLinkItem = { to: string; label: TranslationKey; icon: NavIconName };

const OPEN_PROJECTS_KEY = 'nivio.chat.openProjects';
const PROJECTS_COLLAPSED_KEY = 'nivio.chat.projectsCollapsed';
const RECENT_COLLAPSED_KEY = 'nivio.chat.recentCollapsed';
const PROJECT_PREVIEW = 3;

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Remembering the choice is a convenience; the history works without it.
  }
}

function readOpenProjects(): Set<string> {
  try {
    const parsed: unknown = JSON.parse(readStored(OPEN_PROJECTS_KEY) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

export function Sidebar({
  branding,
  user,
  product,
  mobileOpen = false,
  onMobileClose,
}: {
  branding?: Branding;
  user: ApiUser;
  product: Product;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) {
  const { data: items, isLoading, isError, refetch } = useRecentConversations();
  const busyIds = useBusyConversationIds();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const del = useDeleteConversation();
  const patch = usePatchConversation();
  const { data: projects } = useProjects();
  const { data: skills } = useSkills(product.capabilities.skills);
  const { data: automations } = useAutomations(false, product.capabilities.automations);
  const attention = automations?.attention ?? automations?.unread ?? 0;
  const openingActionDialog = useRef(false);
  const { confirm, confirmDialog } = useConfirmDialog();
  const { prompt, promptDialog } = usePromptDialog();
  const t = useT();
  const language = useLanguage();
  const copy = SHELL_COPY[language];
  const toolLinks: NavLinkItem[] =
    product.profile === 'light'
      ? []
      : [
          ...(product.capabilities.projects
            ? [{ to: '/projects', label: 'projects', icon: 'projects' } as const]
            : []),
          ...(product.capabilities.skills
            ? [{ to: '/skills', label: 'skills', icon: 'skills' } as const]
            : []),
          ...(product.capabilities.artifacts
            ? [{ to: '/artifacts', label: 'artifacts', icon: 'artifacts' } as const]
            : []),
          ...(product.capabilities.automations
            ? [{ to: '/automations', label: 'aiTasks', icon: 'tasks' } as const]
            : []),
          ...(product.capabilities.agents && user.agentsEnabled
            ? [{ to: '/agents', label: 'agents', icon: 'agents' } as const]
            : []),
        ];

  const projectNames = useMemo(
    () => new Map((projects ?? []).map((project) => [project.id, project.name])),
    [projects],
  );

  const orderedItems = useMemo(
    () => [...(items ?? [])].sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)),
    [items],
  );
  const showProjects = product.profile !== 'light' && product.capabilities.projects;
  // Project chats live under their project; while the projects load they stay out of "Recent"
  // so they do not flash there.
  const isProjectChat = (item: ConversationListItem) =>
    showProjects &&
    !!item.projectId &&
    (projects === undefined || projectNames.has(item.projectId));
  const recentItems = orderedItems.filter((item) => !isProjectChat(item)).slice(0, 15);
  const activeItem = orderedItems.find((item) => item.id === conversationId);
  if (
    activeItem &&
    !isProjectChat(activeItem) &&
    !recentItems.some((item) => item.id === activeItem.id)
  )
    recentItems.push(activeItem);

  const [projectsCollapsed, setProjectsCollapsed] = useState(
    () => readStored(PROJECTS_COLLAPSED_KEY) === '1',
  );
  const toggleProjectsSection = () => {
    setProjectsCollapsed((current) => {
      writeStored(PROJECTS_COLLAPSED_KEY, current ? '0' : '1');
      return !current;
    });
  };
  const [recentCollapsed, setRecentCollapsed] = useState(
    () => readStored(RECENT_COLLAPSED_KEY) === '1',
  );
  const toggleRecentSection = () => {
    setRecentCollapsed((current) => {
      writeStored(RECENT_COLLAPSED_KEY, current ? '0' : '1');
      return !current;
    });
  };
  const createConversation = useCreateConversation();
  const { data: models } = useModels();
  const lastModelId = useUi((state) => state.lastModelId);
  // A new chat in a project starts like one from the project page: the project's default
  // knowledge, no fixed title (the title is generated from the first question).
  async function startProjectChat(project: { id: string }) {
    const modelId =
      lastModelId && models?.some((model) => model.id === lastModelId)
        ? lastModelId
        : models?.[0]?.id;
    if (!modelId) return;
    const entry = projects?.find((item) => item.id === project.id);
    const defaults = entry?.defaultDatasetGroups;
    const skillSlugs = projectSkills(entry, skills).map(skillSelectionKey);
    const conversation = await createConversation.mutateAsync({
      modelId,
      projectId: project.id,
      ...(defaults?.length ? { enabledDatasetGroups: defaults } : {}),
      ...(skillSlugs.length ? { selectedSkillSlugs: skillSlugs } : {}),
    });
    setOpenProjects((current) => new Set(current).add(project.id));
    navigate(`/c/${conversation.id}`);
    onMobileClose?.();
  }
  const [openProjects, setOpenProjects] = useState<Set<string>>(readOpenProjects);
  // Opening a conversation reveals its project once; after that the group can be collapsed again.
  const activeProjectId = activeItem?.projectId ?? null;
  useEffect(() => {
    if (!activeProjectId) return;
    setOpenProjects((current) => {
      if (current.has(activeProjectId)) return current;
      const next = new Set(current).add(activeProjectId);
      writeStored(OPEN_PROJECTS_KEY, JSON.stringify([...next]));
      return next;
    });
  }, [activeProjectId, conversationId]);
  const toggleProject = (id: string) => {
    setOpenProjects((current) => {
      const next = new Set(current);
      if (!next.delete(id)) next.add(id);
      writeStored(OPEN_PROJECTS_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  // Projects with their chats, the most recently active project first.
  const projectGroups = useMemo(() => {
    if (!showProjects) return [];
    const byProject = new Map<string, ConversationListItem[]>();
    for (const item of orderedItems) {
      if (!item.projectId || !projectNames.has(item.projectId)) continue;
      byProject.set(item.projectId, [...(byProject.get(item.projectId) ?? []), item]);
    }
    return [...byProject].map(([id, rows]) => ({ id, name: projectNames.get(id) ?? '', rows }));
  }, [orderedItems, projectNames, showProjects]);

  const historyGroups = useMemo(() => {
    const now = new Date();
    const groups: Array<{
      key: GroupKey;
      rows: Array<{ item: ConversationListItem; meta: string }>;
    }> = [];
    for (const item of recentItems) {
      const date = new Date(item.updatedAt);
      const days = conversationDay(date, now);
      const key = groupOf(days);
      let group = groups.find((entry) => entry.key === key);
      if (!group) {
        group = { key, rows: [] };
        groups.push(group);
      }
      group.rows.push({ item, meta: metaOf(date, days, language) });
    }
    return groups;
  }, [recentItems, language]);

  const { data: projectCounts } = useProjectConversationCounts();

  function startNewConversation() {
    // A new conversation starts with an empty composer; never carry over an earlier question.
    if ('new' in useDrafts.getState().entries)
      useDrafts.getState().update('new', { text: '', files: [] });
    navigate('/');
    onMobileClose?.();
  }

  function openSearch(projectId = '') {
    navigate(projectId ? `/conversations?project=${projectId}` : '/conversations');
    onMobileClose?.();
  }

  // Shortcuts shown in the sidebar: ⌘K opens the conversation search, ⌘N starts a new chat.
  const shortcuts = useRef({ startNewConversation, openSearch });
  shortcuts.current = { startNewConversation, openSearch };
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      const key = event.key.toLowerCase();
      if (key === 'k') {
        event.preventDefault();
        shortcuts.current.openSearch();
      } else if (key === 'n') {
        event.preventDefault();
        shortcuts.current.startNewConversation();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const renderRow = ({ item: c, meta }: { item: ConversationListItem; meta: string }) => {
    const active = c.id === conversationId;
    const projectName = c.projectId ? projectNames.get(c.projectId) : null;
    const title = conversationTitle(c.title, t);
    return (
      <div key={c.id} className={clsx('nv-row nv-hrow', active && 'nv-on')}>
        <Link
          to={`/c/${c.id}`}
          className="nv-t"
          aria-current={active ? 'page' : undefined}
          title={projectName ? `${title} · ${projectName}` : title}
          onClick={onMobileClose}
        >
          {title}
        </Link>
        {busyIds.includes(c.id) && (
          <span
            className="nv-busy"
            role="status"
            aria-label={t('generatingInBackground')}
            title={t('generatingInBackground')}
          />
        )}
        <span className="nv-meta">{meta}</span>
        <Popover.Root>
          <Popover.Trigger
            className="nv-hmore"
            aria-label={`${t('conversationActions')}: ${title}`}
            title={t('conversationActions')}
          >
            <span aria-hidden="true">···</span>
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="end"
              sideOffset={4}
              className="nv-root nv-menu"
              aria-label={t('conversationActions')}
              onCloseAutoFocus={(event) => {
                if (openingActionDialog.current) {
                  event.preventDefault();
                  openingActionDialog.current = false;
                }
              }}
            >
              <Popover.Close asChild>
                <button
                  type="button"
                  className="nv-menu-item"
                  onClick={async () => {
                    openingActionDialog.current = true;
                    const next = await prompt({
                      title: t('renameConversation'),
                      initialValue: title,
                      confirmLabel: t('save'),
                    });
                    if (!next || next === title) return;
                    patch.mutate({ id: c.id, patch: { title: next } });
                  }}
                >
                  {t('rename')}
                </button>
              </Popover.Close>
              {showProjects && projectNames.size > 0 && (
                <>
                  <hr className="nv-menu-sep" />
                  <div className="nv-menu-label">{t('moveToProject')}</div>
                  <div className="nv-menu-scroll">
                    {[...projectNames]
                      .filter(([id]) => id !== c.projectId)
                      .map(([id, name]) => (
                        <Popover.Close asChild key={id}>
                          <button
                            type="button"
                            className="nv-menu-item nv-menu-project"
                            onClick={() => patch.mutate({ id: c.id, patch: { projectId: id } })}
                          >
                            <LogoChip size="sm" name={name} />
                            <span className="nv-menu-name">{name}</span>
                          </button>
                        </Popover.Close>
                      ))}
                  </div>
                  {c.projectId && (
                    <Popover.Close asChild>
                      <button
                        type="button"
                        className="nv-menu-item"
                        onClick={() => patch.mutate({ id: c.id, patch: { projectId: null } })}
                      >
                        {t('removeFromProject')}
                      </button>
                    </Popover.Close>
                  )}
                </>
              )}
              <hr className="nv-menu-sep" />
              <Popover.Close asChild>
                <button
                  type="button"
                  className="nv-menu-item nv-danger"
                  onClick={async () => {
                    openingActionDialog.current = true;
                    const ok = await confirm({
                      title: t('deleteConversation'),
                      description: `${t('deleteConversationConfirmPrefix')} "${title}"? ${t(
                        'deleteConversationConfirmSuffix',
                      )}`,
                      confirmLabel: t('delete'),
                      tone: 'danger',
                    });
                    if (!ok) return;
                    del.mutate(c.id, {
                      onSuccess: () => {
                        if (active) navigate('/');
                      },
                    });
                  }}
                >
                  {t('delete')}
                </button>
              </Popover.Close>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    );
  };

  return (
    <aside
      id="primary-navigation"
      aria-label={t('recentConversations')}
      className={clsx('nv-nav nv-chat', mobileOpen && 'nv-nav-open')}
    >
      <SidebarResizeHandle />
      <div className="nv-brandbar">
        <BrandIdentity
          branding={branding}
          product="chat"
          homeTo="/"
          {...(onMobileClose ? { onHomeClick: onMobileClose } : {})}
        />
        <button
          type="button"
          className="nv-navclose"
          aria-label={t('closeNavigation')}
          title={t('closeNavigation')}
          onClick={onMobileClose}
        >
          <XIcon />
        </button>
      </div>

      <button type="button" className="nv-row nv-primary" onClick={startNewConversation}>
        <NavIcon name="plus" />
        <span className="nv-t">{copy.newChat}</span>
        <kbd>⌘ N</kbd>
      </button>

      <div className="nv-ngrid">
        <button type="button" className="nv-row" onClick={() => openSearch()}>
          <NavIcon name="search" />
          <span className="nv-t">{copy.search}</span>
        </button>
        {toolLinks.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-tour={`nav-${item.icon}`}
            className={({ isActive }) => clsx('nv-row', isActive && 'nv-on')}
            onClick={onMobileClose}
          >
            <NavIcon name={item.icon} />
            <span className="nv-t">{t(item.label)}</span>
            {item.to === '/automations' && attention > 0 && (
              <span
                className="nv-badge"
                title={t('attentionBadge')}
                role="img"
                aria-label={`${t('attentionBadge')}: ${attention}`}
              >
                {attention}
              </span>
            )}
          </NavLink>
        ))}
      </div>

      <nav className="nv-hist" aria-label={t('recentConversations')}>
        {isLoading && <div className="nv-hint-row">{t('loading')}</div>}
        {!isLoading && !isError && items?.length === 0 && (
          <div className="nv-hint-row">{t('noConversations')}</div>
        )}
        {isError && (
          <div role="alert" className="nv-hint-row nv-hint-err">
            {t('historyLoadFailed')}{' '}
            <button type="button" className="nv-linkbtn" onClick={() => void refetch()}>
              {t('retryHistory')}
            </button>
          </div>
        )}
        {projectGroups.length > 0 && (
          <div>
            <button
              type="button"
              className="nv-head nv-headbtn"
              aria-expanded={!projectsCollapsed}
              onClick={toggleProjectsSection}
            >
              <span
                className={clsx('nv-chev', !projectsCollapsed && 'nv-open')}
                aria-hidden="true"
              />
              {t('projects')}
              <span className="nv-headcount">{projectGroups.length}</span>
            </button>
            {(projectsCollapsed ? [] : projectGroups).map((project) => {
              const open = openProjects.has(project.id);
              const visible = project.rows.slice(0, PROJECT_PREVIEW);
              const active = project.rows.find((row) => row.id === conversationId);
              if (active && !visible.includes(active)) visible.push(active);
              const now = new Date();
              return (
                <div key={project.id}>
                  <div className="nv-row nv-hrow nv-prow">
                    <button
                      type="button"
                      className="nv-ptoggle"
                      aria-expanded={open}
                      onClick={() => toggleProject(project.id)}
                    >
                      <span className={clsx('nv-chev', open && 'nv-open')} aria-hidden="true" />
                      <LogoChip size="sm" name={project.name} />
                      <span className="nv-t">{project.name}</span>
                    </button>
                    <span className="nv-meta">
                      {projectCounts?.[project.id] ?? project.rows.length}
                    </span>
                    <Popover.Root>
                      <Popover.Trigger
                        className="nv-hmore"
                        aria-label={`${t('projectActions')}: ${project.name}`}
                        title={t('projectActions')}
                      >
                        <span aria-hidden="true">···</span>
                      </Popover.Trigger>
                      <Popover.Portal>
                        <Popover.Content
                          align="end"
                          sideOffset={4}
                          className="nv-root nv-menu"
                          aria-label={t('projectActions')}
                        >
                          <Popover.Close asChild>
                            <button
                              type="button"
                              className="nv-menu-item"
                              onClick={() => void startProjectChat(project)}
                            >
                              {t('newChatInProject')}
                            </button>
                          </Popover.Close>
                          <Popover.Close asChild>
                            <button
                              type="button"
                              className="nv-menu-item"
                              onClick={() => {
                                navigate(`/projects?project=${project.id}`);
                                onMobileClose?.();
                              }}
                            >
                              {t('openProject')}
                            </button>
                          </Popover.Close>
                        </Popover.Content>
                      </Popover.Portal>
                    </Popover.Root>
                  </div>
                  {open && (
                    <div className="nv-kids">
                      {visible.map((item) => {
                        const date = new Date(item.updatedAt);
                        return renderRow({
                          item,
                          meta: metaOf(date, conversationDay(date, now), language),
                        });
                      })}
                      {(projectCounts?.[project.id] ?? project.rows.length) > visible.length && (
                        <button
                          type="button"
                          className="nv-row nv-more"
                          onClick={() => openSearch(project.id)}
                        >
                          <span className="nv-t">
                            {t('showAllOfProject')} (
                            {projectCounts?.[project.id] ?? project.rows.length})
                          </span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {historyGroups.length > 0 && (
          <div>
            <button
              type="button"
              className="nv-head nv-headbtn"
              aria-expanded={!recentCollapsed}
              onClick={toggleRecentSection}
            >
              <span className={clsx('nv-chev', !recentCollapsed && 'nv-open')} aria-hidden="true" />
              {t('recentConversations')}
            </button>
            {!recentCollapsed &&
              historyGroups.map((group) => (
                <div key={group.key}>
                  <div className="nv-head nv-subhead">{copy.groups[group.key]}</div>
                  {group.rows.map(renderRow)}
                </div>
              ))}
          </div>
        )}
      </nav>

      <button type="button" className="nv-row nv-seeall" onClick={() => openSearch()}>
        <span className="nv-t">{t('allConversations')}</span>
        <kbd>⌘ K</kbd>
      </button>

      <AccountFooter user={user} />
      {confirmDialog}
      {promptDialog}
    </aside>
  );
}

type NavIconName = 'plus' | 'search' | 'projects' | 'skills' | 'artifacts' | 'tasks' | 'agents';

/** Navigation icons copied from the mockup (24 px grid, stroke set by `.nv-row svg`). */
const NAV_ICONS: Record<NavIconName, ReactNode> = {
  plus: <path d="M12 5v14M5 12h14" />,
  search: (
    <>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m16 16 5 5" />
    </>
  ),
  projects: <path d="M3 7h7l2 2h9v11H3z" />,
  skills: (
    <>
      <path d="M12 3 4 7l8 4 8-4z" />
      <path d="M4 12l8 4 8-4" />
      <path d="M4 17l8 4 8-4" />
    </>
  ),
  artifacts: (
    <>
      <path d="M7 3h7l5 5v13H7z" />
      <path d="M14 3v5h5" />
    </>
  ),
  tasks: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l3 2" />
    </>
  ),
  agents: (
    <>
      <rect x="4" y="6" width="16" height="14" rx="3" />
      <circle cx="9" cy="12" r="1" />
      <circle cx="15" cy="12" r="1" />
      <path d="M12 3v3M9 16h6" />
    </>
  ),
};

function NavIcon({ name }: { name: NavIconName }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {NAV_ICONS[name]}
    </svg>
  );
}

/** Footer of the mockup: avatar, name, email and a gear. It opens the account menu. */
function AccountFooter({ user }: { user: ApiUser }) {
  const t = useT();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const openingSettings = useRef(false);
  const email = user.email;
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  const name = fullName || nameFromEmail(email);
  return (
    <Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
      <Popover.Trigger
        className="nv-navfoot"
        data-tour="account"
        aria-label={`${t('settings')}: ${email}`}
      >
        <AccountAvatar email={email} photoUrl={user.photoUrl} />
        <span className="nv-who">
          <span className="nv-who-name">{name}</span>
          <span className="nv-meta">{email}</span>
        </span>
        <GearIcon />
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          align="start"
          sideOffset={6}
          className="nv-root nv-menu"
          aria-label={email}
          onCloseAutoFocus={(event) => {
            if (openingSettings.current) {
              event.preventDefault();
              openingSettings.current = false;
            }
          }}
        >
          <div className="nv-menu-head">
            <div>{name}</div>
            <div className="nv-menu-sub">{email}</div>
          </div>
          <div className="nv-menu-sep" />
          <UsageBreakdownPanel enabled={menuOpen} />
          <div className="nv-menu-sep" />
          <button
            type="button"
            className="nv-menu-item"
            onClick={() => {
              openingSettings.current = true;
              setMenuOpen(false);
              navigate('/settings');
            }}
          >
            <MenuIcon name="settings" />
            {t('settings')}
          </button>
          <button
            type="button"
            className="nv-menu-item"
            onClick={async () => {
              stopAllChatStreams();
              useDrafts.getState().clear();
              // Stop notifying this device for the person who signs out (bounded: offline must not block).
              await Promise.race([
                unsubscribeThisDevice(user.id).catch(() => {}),
                new Promise((resolve) => window.setTimeout(resolve, 3000)),
              ]);
              await api.logout().catch(() => {});
              window.location.href = '/login';
            }}
          >
            <MenuIcon name="signOut" />
            {t('signOut')}
          </button>
          <div className="nv-menu-sep" />
          <div className="nv-menu-version">
            <ReleaseNotesDialog />
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

const ICON_PROPS = {
  ariaHidden: true,
  viewBox: '0 0 24 24',
  className: 'h-4 w-4 shrink-0',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function XIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox={ICON_PROPS.viewBox}
      className={ICON_PROPS.className}
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_PROPS.strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox={ICON_PROPS.viewBox}
      className={ICON_PROPS.className}
      fill="none"
      stroke="currentColor"
      strokeWidth={ICON_PROPS.strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.05.05a2 2 0 1 1-2.83 2.83l-.05-.05a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.07A1.7 1.7 0 0 0 9 19.37a1.7 1.7 0 0 0-1.88.34l-.05.05a2 2 0 1 1-2.83-2.83l.05-.05A1.7 1.7 0 0 0 4.6 15 1.7 1.7 0 0 0 3 14H3a2 2 0 1 1 0-4h.07A1.7 1.7 0 0 0 4.6 9 1.7 1.7 0 0 0 4.26 7.1l-.05-.05a2 2 0 1 1 2.83-2.83l.05.05A1.7 1.7 0 0 0 9 4.63 1.7 1.7 0 0 0 10 3.07V3a2 2 0 1 1 4 0v.07A1.7 1.7 0 0 0 15 4.63a1.7 1.7 0 0 0 1.88-.34l.05-.05a2 2 0 1 1 2.83 2.83l-.05.05A1.7 1.7 0 0 0 19.4 9 1.7 1.7 0 0 0 21 10h.07a2 2 0 1 1 0 4H21a1.7 1.7 0 0 0-1.6 1Z" />
    </svg>
  );
}
